import {useEffect, useState} from 'react';
import {useUser} from '../contexts/UserContext';
import {ehrService} from '../services/ehrService';

// Last resolved answer per company, in-memory plus localStorage. Every mount
// otherwise starts at `false` until a Firestore round-trip resolves, which
// made EHR-gated UI (the sidebar's Charting tab, phone-encounter routing)
// visibly pop in after first paint — and shift the nav around it — on every
// load. Linkage changes are rare (an admin toggling the integration), so we
// seed optimistically from the last known answer and let the fetch correct
// it: worst case after an unlink is the tab showing briefly, then hiding once
// the config loads — the mirror image of the old pop-in, but only ever after
// a real config change instead of on every mount.
const LINKED_STORAGE_PREFIX = 'opalite.ehrLinked.';
const linkedCache = new Map<string, boolean>();

function readCachedLinked(companyId: string): boolean | undefined {
  const inMemory = linkedCache.get(companyId);
  if (inMemory !== undefined) return inMemory;
  try {
    const raw = window.localStorage.getItem(LINKED_STORAGE_PREFIX + companyId);
    if (raw === '1') return true;
    if (raw === '0') return false;
  } catch {
    // Storage unavailable (private mode, etc.) — fall through to undefined.
  }
  return undefined;
}

function writeCachedLinked(companyId: string, linked: boolean) {
  linkedCache.set(companyId, linked);
  try {
    window.localStorage.setItem(
      LINKED_STORAGE_PREFIX + companyId,
      linked ? '1' : '0',
    );
  } catch {
    // Best-effort; the in-memory cache still covers this session.
  }
}

// Whether the signed-in user's company has an active EHR integration. Used to
// gate EHR-only flows (e.g. routing a finished phone encounter into the
// telephone-encounter form). Companies we've never resolved before read as
// false until the config loads, and on any error, so non-EHR accounts never
// hit EHR-gated paths.
export function useEhrLinked(): boolean {
  const {userData, mockEhrMode} = useUser();
  const isMockEhrMode = userData?.role === 'superadmin' && Boolean(mockEhrMode);
  // Keep the raw value for the Firestore query (equality there is
  // type-sensitive); stringify only for cache keys.
  const companyId = userData?.companyId || null;
  const cacheKey = companyId ? String(companyId) : null;
  const [linked, setLinked] = useState(() =>
    cacheKey ? (readCachedLinked(cacheKey) ?? false) : false,
  );

  useEffect(() => {
    if (isMockEhrMode) {
      setLinked(true);
      return undefined;
    }
    if (!companyId || !cacheKey) {
      setLinked(false);
      return undefined;
    }
    // Re-seed on company change (the initializer only runs on first mount).
    const cached = readCachedLinked(cacheKey);
    setLinked(cached ?? false);
    let cancelled = false;
    ehrService
      .fetchEhrConfig(companyId)
      .then(config => {
        if (cancelled) return;
        const next = ehrService.isConfigActive(config);
        writeCachedLinked(cacheKey, next);
        setLinked(next);
      })
      .catch(() => {
        // Transient failure: keep the last known answer rather than yanking
        // EHR-gated UI; unknown companies still resolve to false.
        if (!cancelled) setLinked(cached ?? false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, cacheKey, isMockEhrMode]);

  return isMockEhrMode || linked;
}

export default useEhrLinked;
